import React, { useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AppFooter from '@core/components/AppFooter';
import { ErrorResponse } from '@core/types/errorResponse';
import { resetPasswordAsync } from '@features/auth/services/resetPasswordSvc';
import { ResetPasswordRequest } from '@features/auth/types/resetPassword';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import loginImage from '../login/login.png';
import '../login/login.css';
import InfoTooltip from '@core/components/InfoTooltip';

type FieldErrors = Partial<Record<keyof ResetPasswordRequest, string[]>>;

const ResetPassword: React.FC = () => {
  const [form, setForm] = useState<ResetPasswordRequest>({ email: '', password: '', password_confirmation: '', password_reset_token: '' });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const showError = useApiError();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Clear error when field changes
    if (formErrors[name as keyof ResetPasswordRequest]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name as keyof ResetPasswordRequest];
        return updated;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const response = await resetPasswordAsync(form);

      if (response.isSuccess()) {
        setFormErrors({});
        showSuccess("Reset Password has been successful");
        navigate("/");
      } else {
        const error = response.getError() as ErrorResponse;
        setFormErrors((error?.errors as FieldErrors) ?? {});
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="login-wrapper min-vh-100 d-flex flex-column">
      <main
        id="main"
        className="flex-grow-1 d-flex align-items-center justify-content-center py-4"
        tabIndex={-1}
        data-testid="reset-password-page"
      >
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="login-card shadow-lg border-0" data-testid="reset-password-card">
                <Row className="g-0">
                  <Col
                    md={6}
                    className="d-none d-md-flex align-items-center justify-content-center login-image bg-light"
                    data-testid="reset-password-illustration-panel"
                  >
                    <img src={loginImage} alt="NTCOG Logo" className="img-fluid" />
                  </Col>
                  <Col md={6} className="d-flex align-items-center">
                    <Card.Body className="p-4 p-md-5 w-100 auth-card__body">
                      <h1 className="mb-4 text-center fw-bold text-dark auth-page-heading">Reset Password</h1>
                      <p className="text-center text-muted mb-4">
                        Paste the token from your email and choose a new password for your account.
                      </p>

                      <Form
                        noValidate
                        onSubmit={handleSubmit}
                        className="login-form"
                        aria-label="Reset password form"
                        data-testid="reset-password-form"
                      >
                        <Form.Group className="mb-4" controlId="password_reset_token">
                          <Form.Label>Reset Token<InfoTooltip text="Paste the token from the email we sent you." /></Form.Label>
                          <Form.Control
                            type="text"
                            name="password_reset_token"
                            placeholder="Reset Token"
                            value={form.password_reset_token}
                            onChange={handleChange}
                            isInvalid={!!formErrors.password_reset_token}
                            data-testid="reset-password-token-input"
                          />
                          <Form.Control.Feedback type="invalid" role="alert">
                            {formErrors.password_reset_token?.[0]}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="email">
                          <Form.Label>Email<InfoTooltip text="The email address for your account." /></Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={form.email}
                            onChange={handleChange}
                            isInvalid={!!formErrors.email}
                            data-testid="reset-password-email-input"
                          />
                          <Form.Control.Feedback type="invalid" role="alert">
                            {formErrors.email?.[0]}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="password">
                          <Form.Label>New Password<InfoTooltip text="Choose a new password. Use a mix of letters, numbers and symbols for strength." /></Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            placeholder="New Password"
                            value={form.password}
                            onChange={handleChange}
                            isInvalid={!!formErrors.password}
                            data-testid="reset-password-new-password-input"
                          />
                          <Form.Control.Feedback type="invalid" role="alert">
                            {formErrors.password?.[0]}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="password_confirmation">
                          <Form.Label>Confirm Password<InfoTooltip text="Re-enter your new password to confirm." /></Form.Label>
                          <Form.Control
                            type="password"
                            name="password_confirmation"
                            placeholder="Confirm Password"
                            value={form.password_confirmation}
                            onChange={handleChange}
                            isInvalid={!!formErrors.password_confirmation}
                            data-testid="reset-password-confirm-password-input"
                          />
                          <Form.Control.Feedback type="invalid" role="alert">
                            {formErrors.password_confirmation?.[0]}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          className="w-100 mb-3"
                          disabled={loading}
                          aria-busy={loading}
                          data-testid="reset-password-submit-button"
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Resetting…
                            </>
                          ) : (
                            'Submit'
                          )}
                        </Button>

                        <div className="auth-secondary-link-wrap">
                          <Button
                            variant="link"
                            className="text-muted p-0 forgot-password auth-secondary-link"
                            onClick={() => navigate('/login')}
                            data-testid="reset-password-login-link"
                          >
                            Back to login
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>
      <AppFooter />
    </div>
  );
};

export default ResetPassword;




